import React, { useState } from 'react';
import { FileInput, Plus, Search } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { PRESET_RECEIVED_INVOICES, ReceivedInvoice, STORAGE_KEYS, formatEuro } from './financeMasters';
import { useStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

interface FinanceReceivedInvoicesViewProps {
  privacyMode: boolean;
  onNavigate: (groupId: string, subScreenId?: string) => void;
}

export const FinanceReceivedInvoicesView = ({ privacyMode, onNavigate }: FinanceReceivedInvoicesViewProps) => {
  const [invoices] = useStoredList<ReceivedInvoice>(STORAGE_KEYS.receivedInvoices, PRESET_RECEIVED_INVOICES);
  const [searchQuery, setSearchQuery] = useState('');

  const query = searchQuery.toLowerCase();
  const filteredInvoices = invoices.filter(inv =>
    inv.id.toLowerCase().includes(query) ||
    inv.supplier.toLowerCase().includes(query) ||
    inv.cif.toLowerCase().includes(query) ||
    inv.concept.toLowerCase().includes(query)
  );

  const columns: ColumnDef<ReceivedInvoice>[] = [
    {
      id: 'id',
      header: 'Nº REGISTRO',
      render: (inv) => <span className="fin-master__code">{inv.id}</span>
    },
    {
      id: 'date',
      header: 'FECHA',
      render: (inv) => <span className="fin-master__sub">{inv.date}</span>
    },
    {
      id: 'supplier',
      header: 'PROVEEDOR / CIF',
      render: (inv) => (
        <>
          <div className="fin-master__name">{inv.supplier}</div>
          <div className="fin-master__sub">CIF: {inv.cif}</div>
        </>
      )
    },
    {
      id: 'concept',
      header: 'CONCEPTO',
      render: (inv) => (
        <>
          <div className="fin-master__text">{inv.concept}</div>
          <span className="fin-master__chip">{inv.account}</span>
        </>
      )
    },
    {
      id: 'base',
      header: 'BASE',
      align: 'right',
      render: (inv) => <span className="fin-master__amount">{formatEuro(inv.base, privacyMode)}</span>
    },
    {
      id: 'vat',
      header: 'IVA SOPORTADO',
      align: 'right',
      render: (inv) => <span className="fin-master__amount">{formatEuro(inv.vat, privacyMode)}</span>
    },
    {
      id: 'total',
      header: 'TOTAL FACTURA',
      align: 'right',
      render: (inv) => <span className="fin-master__amount">{formatEuro(inv.total, privacyMode)}</span>
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (inv) => (
        <span className={`module-badge ${inv.status === 'Pagada' ? 'paid' : 'draft'}`}>{inv.status}</span>
      )
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR Nº DE REGISTRO, PROVEEDOR, CIF O CONCEPTO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={() => onNavigate('acreedores', 'crear_factura_recibida')} className="module-create-btn">
          <Plus size={14} /> Nueva_Factura_Recibida
        </button>
      </div>

      <DataTable
        data={filteredInvoices}
        columns={columns}
        title={<><FileInput size={14} className="module-panel-title-icon" /> Facturas_Recibidas_De_Proveedores</>}
        subtitle={`TOTAL: ${filteredInvoices.length}`}
        getRowId={(inv) => inv.id}
        emptyText="No se encontraron facturas recibidas."
      />
    </div>
  );
};

export default FinanceReceivedInvoicesView;
